import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import PatternPreview from './Preview'
import { IPatternAssets, Note, PatternDraft, PatternSelections, Topic } from '@/types'
import { Assets } from '@/utils/pattern-assets'
import { useGetTopicDetails } from '@/hooks/api/dashboard'
import { PatternAssetsMap, TopicType } from '@/utils/constants'
import { SetCanvasArea, ResizeBabylonScene } from './renderer/main'
import { useBrowserQuery } from '@/hooks/common'

interface PatternPreviewPageProps {
  topicID: string
}

const PatternPreviewPage: React.FC<PatternPreviewPageProps> = ({ topicID }) => {
  const canvaRef = useRef<any>(null)

  const query = useBrowserQuery()
  const viewStep: string = query.get('step') || '1'

  useEffect(() => {
    initializePatternData()
    SetCanvasArea(0)
  }, [])

  useEffect(() => {
    if (canvaRef.current) {
      ResizeBabylonScene()
    }
  }, [viewStep])

  useEffect(() => {
    const handleResize = () => {
      if (canvaRef.current) {
        ResizeBabylonScene()
      }
    }

    window.addEventListener('resize', handleResize)

    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handlePatternLoad = () => {
    console.log('Pattern loaded')
    canvaRef.current?.updateOverlayStatus(false)
  }
  const onAssetsUpdate = () => console.log('Assets updated')
  const toggleFullscreen = () => console.log('Toggled full screen')
  const handlePatternEdit = () => console.log('Pattern edited')
  const setShowAccentSliders = () => console.log('Accent sliders toggled')
  const setAccentScale = (value: number) => console.log('Accent scale set:', value)
  const setAccentTiling = (value: number) => console.log('Accent tiling set:', value)
  const setAccentRotation = (value: number) => console.log('Accent rotation set:', value)
  const setPrintTiling = (value: number) => console.log('Print tiling set:', value)
  const setCameraHorizontalPosition = (value: number) =>
    console.log('Camera horizontal set : ', value)
  const [assets, setAssets] = useState<IPatternAssets>(Assets)
  const [topic, setTopic] = useState<Topic | null>(null)
  const [isError, setIsError] = useState(false)
  const getTopicDetails = useGetTopicDetails()
  const [patternData, setPatternData] = useState<PatternDraft>({})

  const [selectedAssets, setSelectedAssets] = useState<PatternSelections>({
    // boarder: {
    //   width: 0,
    //   type: 'square',
    // },
    matting: {
      width: 0,
    },
    clicked: false,
  })

  const getTopic = async (): Promise<Topic | undefined> => {
    // setLoading(true)
    let topicData
    try {
      topicData = await getTopicDetails.mutateAsync({ topicID })
      console.log('Get topic from id ', topicID, ' result : ', topicData)
      setTopic(topicData)
      // setLoading(false)
      return topicData
    } catch (e) {
      setIsError(true)
      // setLoading(false)
    }
  }

  const initializePatternData = async () => {
    const topic = await getTopic()
    if (!topic) return

    const defaultTemplateID =
        topic.topicType == TopicType.PATTERN.toString()
            ? 2
            : topic.topicType == TopicType.GALLERY.toString()
                ? 400
                : 2

    const draft: PatternDraft = { templateID: defaultTemplateID }
    
    const sortedNotes = [...topic.notes].sort((a, b) => {
      const dateA = a.dateUpdated || a.dateCreated || '';
      const dateB = b.dateUpdated || b.dateCreated || '';
      return dateB.localeCompare(dateA); 
    });

    const processedNames = new Set<string>();

    const notesByType = new Map<string, Note>()

    sortedNotes.forEach((note: Note) => {
      if (note.noteType === '8' || note.noteType === '9' || note.noteType === '1') return
      // if (note.noteText === 'null' || note.noteText == null) return

      if (!processedNames.has(note.noteName)) {
        processedNames.add(note.noteName);

        try {
          const parsedValue = JSON.parse(note.noteText)

          // Debug specific keys if needed
          if (note.noteName === 'frameID') {
            console.log(`📥 [Loader] Loaded frameID:`, parsedValue);
          }

          // Direct assignment using the note's name as the key
          draft[note.noteName as keyof PatternDraft] = parsedValue

        } catch (e) {
          console.error(`🚨 Failed to parse note ${note.noteName}:`, e)
        }
      }
    })

    console.log('✅ [Preview] Final unified draft:', draft)
    setPatternData(draft)
  }

  useEffect(() => {
    console.log('PatternPreviewPage loaded with topicID:', topicID)
  }, [topicID])

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
      }}
    >
      <PatternPreview
        ref={canvaRef}
        selection={selectedAssets}
        selectedTab=''
        assets={assets}
        patternData={patternData}
        textData=''
        isEditMode={false}
        isPagePreview={true}
        addScreenshot={() => {}}
        onPatternRestore={handlePatternLoad}
        onAssetsUpdate={onAssetsUpdate}
        onToggleFullScreenMode={toggleFullscreen}
        onEdit={handlePatternEdit}
        toggleSliderVisibilityCallBack={setShowAccentSliders}
        updateSliderFromAccentScaleCallBack={setAccentScale}
        updateSliderFromAccentNumberCallBack={setAccentTiling}
        updateSliderFromAccentRotationCallBack={setAccentRotation}
        updateSliderFromPrintTilingCallBack={setPrintTiling}
        updateSliderFromCameraHorizontalCallback={setCameraHorizontalPosition}
        onResetTemplate={() => {}}
      />
    </Box>
  )
}

export default PatternPreviewPage
